"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "@/lib/firestore-instrumented";
import { db } from "@/firebase/config";
import ChronicleIncidentDrawer from "@/components/chronicles/ChronicleIncidentDrawer";
import ConfirmDialog from "@/components/ConfirmDialog";
import LinkifiedCardText from "@/components/LinkifiedCardText";
import { showToast } from "@/components/ToastCenter";
import {
  buildChroniclePath,
  buildChronicleShareRoutePath,
  buildIncidentPath,
  chronicleIdFor,
  formatRelativeDate,
  toDateSafe,
} from "@/lib/chronicles";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  Edit3,
  MapPin,
  ScanText,
  Share2,
  Sparkles,
  Trash,
} from "lucide-react";
import ChronicleSectionMasthead from "@/components/chronicles/ChronicleSectionMasthead";
import IncidentCard from "@/components/chronicles/IncidentCard";
import ObjectActionMenu from "@/components/ui/ObjectActionMenu";
import { resolvePrimaryLocationSelection, syncIncidentPrimaryLocation } from "@/lib/chroniclePrimaryLocation";
import { shareWithSystem } from "@/lib/nativeShare";
import { usePortal } from "@/context/PortalContext";
import { getShareBranding, shareTitle } from "@/lib/portal/shareBranding.mjs";

const ShareModal = dynamic(() => import("@/components/ShareModal"), {
  ssr: false,
  loading: () => null,
});

export default function ChronicleClient({ hubSlug, chronicleSlug }) {
  const { user } = useAuth();
  const portal = usePortal();
  const shareBranding = useMemo(() => getShareBranding(portal), [portal]);
  const [hub, setHub] = useState(null);
  const [hubMember, setHubMember] = useState(null);
  const [chronicle, setChronicle] = useState(null);
  const [chronicleRole, setChronicleRole] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loadingChronicle, setLoadingChronicle] = useState(true);
  const [loadingIncidents, setLoadingIncidents] = useState(true);
  const [error, setError] = useState("");
  const [drawerState, setDrawerState] = useState({ open: false, mode: "create", incident: null });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingIncident, setDeletingIncident] = useState(false);
  const [deleteChronicleOpen, setDeleteChronicleOpen] = useState(false);
  const [deletingChronicle, setDeletingChronicle] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState({ name: "", description: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [availableLocationEntities, setAvailableLocationEntities] = useState([]);
  const [incidentStatusFilter, setIncidentStatusFilter] = useState("all");
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);

  const chronicleId = useMemo(() => chronicleIdFor(hubSlug, chronicleSlug), [hubSlug, chronicleSlug]);

  const memberRole = String(hubMember?.role || "").toLowerCase();
  const isHubOwner = Boolean(hub?.ownerUid && user?.uid === hub.ownerUid);
  const isHubManager = isHubOwner || memberRole === "moderator";
  const role = String(chronicleRole?.role || "").toLowerCase();
  const isChronicleOwner = role === "owner" || chronicle?.createdBy === user?.uid;
  const isChronicleManager = isHubManager || role === "owner" || role === "moderator" || chronicle?.createdBy === user?.uid;
  const canContribute = isChronicleManager || role === "contributor";
  const canPublish = isChronicleManager;
  const canDeleteChronicle = isChronicleOwner;
  const canEditChronicleSettings = isChronicleOwner;
  const canReadDrafts = canContribute;
  const canFilterIncidentStatus = isChronicleOwner;

  const buildDateParts = useCallback((value) => {
    const date = toDateSafe(value);
    if (!date) return { year: "No date", monthDay: "TBD" };
    return {
      year: date.toLocaleDateString(undefined, { year: "numeric" }),
      monthDay: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    };
  }, []);

  const buildSharePayload = useCallback(() => {
    if (!chronicle?.slug) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const path = buildChronicleShareRoutePath(hubSlug, chronicle.slug || chronicleSlug);
    const url = origin ? `${origin}${path}` : path;
    const cleanDesc = String(chronicle.description || "").replace(/\s+/g, " ").trim();
    const snippet = cleanDesc
      ? cleanDesc.length > 140
        ? `${cleanDesc.slice(0, 140).trim()}…`
        : cleanDesc
      : `Follow this timeline on ${shareBranding.siteName}.`;

    return {
      url,
      title: shareTitle(chronicle.name || chronicle.slug, shareBranding),
      text: `${snippet}\n\n— Chronicle in +${hubSlug} on ${shareBranding.siteName}`,
    };
  }, [chronicle?.description, chronicle?.name, chronicle?.slug, chronicleSlug, hubSlug, shareBranding]);

  const handleShare = useCallback(async () => {
    const payload = buildSharePayload();
    if (!payload) return;
    if (await shareWithSystem(payload)) return;

    setSharePayload(payload);
    setShareOpen(true);
  }, [buildSharePayload]);

  useEffect(() => {
    if (!chronicle || !settingsOpen) return;
    setSettingsDraft({
      name: String(chronicle.name || "").trim(),
      description: String(chronicle.description || ""),
    });
  }, [chronicle?.id, chronicle?.name, chronicle?.description, settingsOpen]);

  useEffect(() => {
    const qHub = query(collection(db, "hubs"), where("slug", "==", hubSlug), limit(1));
    const unsub = onSnapshot(
      qHub,
      (snap) => {
        const first = snap.docs[0];
        setHub(first ? { id: first.id, ...first.data() } : {});
      },
      (err) => {
        console.error("Chronicle hub load failed:", err);
      }
    );
    return () => unsub();
  }, [hubSlug]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user?.uid || !hubSlug) {
        if (mounted) setHubMember(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "hubMembers", `${hubSlug}_${user.uid}`));
        if (!mounted) return;
        setHubMember(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      } catch {
        if (mounted) setHubMember(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [hubSlug, user?.uid]);

  useEffect(() => {
    let active = true;
    setLoadingChronicle(true);
    setError("");
    (async () => {
      try {
        let snap = await getDoc(doc(db, "chronicles", chronicleId));
        if (!snap.exists()) {
          const fallbackQ = query(
            collection(db, "chronicles"),
            where("hubSlug", "==", hubSlug),
            where("slug", "==", chronicleSlug),
            limit(1)
          );
          const fallback = await getDocs(fallbackQ);
          snap = fallback.docs[0] || null;
          if (!snap) {
            if (active) {
              setChronicle({});
              setLoadingChronicle(false);
            }
            return;
          }
        }
        if (!active) return;
        const docSnap = "id" in snap ? snap : null;
        if (docSnap) setChronicle({ id: docSnap.id, ...docSnap.data() });
      } catch (err) {
        console.error("Chronicle load failed:", err);
        if (active) {
          setError("Could not load chronicle.");
          setChronicle({});
        }
      } finally {
        if (active) setLoadingChronicle(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [chronicleId, hubSlug, chronicleSlug]);

  useEffect(() => {
    if (!chronicle?.id || !user?.uid) {
      setChronicleRole(null);
      return;
    }
    const roleRef = doc(db, "chronicles", chronicle.id, "roles", user.uid);
    const unsub = onSnapshot(
      roleRef,
      (snap) => setChronicleRole(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => setChronicleRole(null)
    );
    return () => unsub();
  }, [chronicle?.id, user?.uid]);

  useEffect(() => {
    if (!canFilterIncidentStatus && incidentStatusFilter !== "all") {
      setIncidentStatusFilter("all");
    }
  }, [canFilterIncidentStatus, incidentStatusFilter]);

  useEffect(() => {
    if (!hubSlug) return undefined;
    const entityQ = query(collection(db, "entities"), where("hubSlug", "==", hubSlug), limit(200));
    const unsub = onSnapshot(
      entityQ,
      (snap) => {
        const items = snap.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter(
            (item) =>
              String(item.type || "").toLowerCase() === "location" &&
              String(item.status || "active") !== "hidden"
          )
          .sort((a, b) =>
            String(a.displayName || "").localeCompare(String(b.displayName || ""), undefined, {
              sensitivity: "base",
            })
          );
        setAvailableLocationEntities(items);
      },
      () => setAvailableLocationEntities([])
    );
    return () => unsub();
  }, [hubSlug]);

  useEffect(() => {
    if (!chronicle?.id) return undefined;
    setLoadingIncidents(true);
    const base = collection(db, "chronicles", chronicle.id, "incidents");
    const incidentQ = canReadDrafts
      ? query(base, orderBy("sortKey", "desc"), limit(200))
      : query(base, where("status", "==", "published"), orderBy("sortKey", "desc"), limit(200));
    const unsub = onSnapshot(
      incidentQ,
      (snap) => {
        setIncidents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingIncidents(false);
      },
      (err) => {
        console.error("Chronicle incidents failed:", err);
        setError("Could not load incidents.");
        setLoadingIncidents(false);
      }
    );
    return () => unsub();
  }, [chronicle?.id, canReadDrafts]);

  const visibleIncidents = useMemo(() => {
    if (!canFilterIncidentStatus || incidentStatusFilter === "all") return incidents;
    return incidents.filter((incident) => String(incident.status || "draft").toLowerCase() === incidentStatusFilter);
  }, [canFilterIncidentStatus, incidentStatusFilter, incidents]);

  const refreshStats = useCallback(async (chronicleDocId) => {
    const incidentsCol = collection(db, "chronicles", chronicleDocId, "incidents");
    const countSnap = await getCountFromServer(incidentsCol);
    const latestSnap = await getDocs(query(incidentsCol, orderBy("sortKey", "desc"), limit(1)));
    const latest = latestSnap.docs[0]?.data()?.sortKey || null;
    await updateDoc(doc(db, "chronicles", chronicleDocId), {
      incidentCount: countSnap.data().count,
      latestIncidentAt: latest || null,
      updatedAt: serverTimestamp(),
    });
  }, []);

  const handleCreateIncident = useCallback(
    async (payload) => {
      if (!chronicle?.id || !user?.uid) return;
      setSaving(true);
      try {
        const status = canPublish ? payload.status : "draft";
        const primaryLocation = await resolvePrimaryLocationSelection({
          db,
          userId: user.uid,
          hubSlug,
          hubDocId: hub?.id || chronicle?.hubDocId || null,
          selection: payload.primaryLocation,
          existingEntities: availableLocationEntities,
        });
        const incidentRef = doc(collection(db, "chronicles", chronicle.id, "incidents"));
        await setDoc(incidentRef, {
          title: payload.title,
          description: payload.description,
          location: primaryLocation?.displayName || "",
          primaryLocationEntityId: primaryLocation?.entityId || null,
          status,
          temporal: payload.temporal,
          sortKey: payload.sortKey,
          authorUid: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastEditedBy: user.uid,
        });
        await syncIncidentPrimaryLocation({
          db,
          chronicleId: chronicle.id,
          incidentId: incidentRef.id,
          userId: user.uid,
          nextPrimary: primaryLocation,
        });
        await refreshStats(chronicle.id);
        setDrawerState({ open: false, mode: "create", incident: null });
        showToast("Incident added.");
      } catch (err) {
        console.error("Create incident failed:", err);
        showToast(err?.message || "Could not create incident.");
      } finally {
        setSaving(false);
      }
    },
    [chronicle?.id, chronicle?.hubDocId, user?.uid, canPublish, refreshStats, hubSlug, hub?.id, availableLocationEntities]
  );

  const handleEditIncident = useCallback(
    async (payload) => {
      if (!chronicle?.id || !drawerState.incident?.id || !user?.uid) return;
      setSaving(true);
      try {
        const canEdit = isChronicleManager || drawerState.incident.authorUid === user.uid;
        if (!canEdit) {
          showToast("You do not have permission to edit this incident.");
          return;
        }
        const status = canPublish ? payload.status : "draft";
        const primaryLocation = await resolvePrimaryLocationSelection({
          db,
          userId: user.uid,
          hubSlug,
          hubDocId: hub?.id || chronicle?.hubDocId || null,
          selection: payload.primaryLocation,
          existingEntities: availableLocationEntities,
        });
        await updateDoc(doc(db, "chronicles", chronicle.id, "incidents", drawerState.incident.id), {
          title: payload.title,
          description: payload.description,
          location: primaryLocation?.displayName || "",
          primaryLocationEntityId: primaryLocation?.entityId || null,
          status,
          temporal: payload.temporal,
          sortKey: payload.sortKey,
          updatedAt: serverTimestamp(),
          lastEditedBy: user.uid,
        });
        await syncIncidentPrimaryLocation({
          db,
          chronicleId: chronicle.id,
          incidentId: drawerState.incident.id,
          userId: user.uid,
          nextPrimary: primaryLocation,
        });
        await refreshStats(chronicle.id);
        setDrawerState({ open: false, mode: "create", incident: null });
        showToast("Incident updated.");
      } catch (err) {
        console.error("Update incident failed:", err);
        showToast(err?.message || "Could not update incident.");
      } finally {
        setSaving(false);
      }
    },
    [chronicle?.id, chronicle?.hubDocId, drawerState.incident, user?.uid, canPublish, isChronicleManager, refreshStats, hubSlug, hub?.id, availableLocationEntities]
  );

  const handleDeleteIncident = useCallback(async () => {
    if (!chronicle?.id || !deleteTarget?.id) return;
    setDeletingIncident(true);
    try {
      await deleteDoc(doc(db, "chronicles", chronicle.id, "incidents", deleteTarget.id));
      await refreshStats(chronicle.id);
      setDeleteTarget(null);
      showToast("Incident deleted.");
    } catch (err) {
      console.error("Delete incident failed:", err);
      showToast(err?.message || "Could not delete incident.");
    } finally {
      setDeletingIncident(false);
    }
  }, [chronicle?.id, deleteTarget?.id, refreshStats]);

  const deleteCollectionInBatches = useCallback(async (colRef) => {
    while (true) {
      const snap = await getDocs(query(colRef, limit(200)));
      if (snap.empty) break;
      const batch = writeBatch(db);
      snap.docs.forEach((item) => batch.delete(item.ref));
      await batch.commit();
    }
  }, []);

  const handleDeleteChronicle = useCallback(async () => {
    if (!chronicle?.id || !canDeleteChronicle) return;
    setDeletingChronicle(true);
    try {
      await deleteCollectionInBatches(collection(db, "chronicles", chronicle.id, "incidents"));
      await deleteCollectionInBatches(collection(db, "chronicles", chronicle.id, "roles"));
      await deleteDoc(doc(db, "chronicles", chronicle.id));
      showToast("Chronicle deleted.");
      window.location.assign(`/h/${encodeURIComponent(hubSlug)}?t=chronicles`);
    } catch (err) {
      console.error("Delete chronicle failed:", err);
      showToast(err?.message || "Could not delete chronicle.");
    } finally {
      setDeletingChronicle(false);
      setDeleteChronicleOpen(false);
    }
  }, [chronicle?.id, canDeleteChronicle, deleteCollectionInBatches, hubSlug]);

  const handleSaveChronicleSettings = useCallback(async () => {
    if (!chronicle?.id || !canEditChronicleSettings) return;
    const nextName = settingsDraft.name.trim();
    if (!nextName) {
      showToast("Chronicle name is required.");
      return;
    }
    setSavingSettings(true);
    try {
      await updateDoc(doc(db, "chronicles", chronicle.id), {
        name: nextName,
        description: settingsDraft.description.trim(),
        updatedAt: serverTimestamp(),
      });
      setChronicle((prev) =>
        prev
          ? {
              ...prev,
              name: nextName,
              description: settingsDraft.description.trim(),
            }
          : prev
      );
      setSettingsOpen(false);
      showToast("Chronicle settings updated.");
    } catch (err) {
      console.error("Chronicle settings update failed:", err);
      showToast(err?.message || "Could not update chronicle settings.");
    } finally {
      setSavingSettings(false);
    }
  }, [chronicle?.id, canEditChronicleSettings, settingsDraft.name, settingsDraft.description]);

  if (loadingChronicle && !chronicle) {
    return (
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-3">
        <div className="card square-mobile border border-[var(--border)] bg-[var(--card)] px-4 py-6 text-sm text-[var(--muted)]">
          Loading chronicle…
        </div>
      </main>
    );
  }

  if (!chronicle || Object.keys(chronicle).length === 0) {
    return (
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-3">
        <div className="card square-mobile border border-[var(--border)] bg-[var(--card)] px-4 py-6 text-sm">Chronicle not found.</div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 pb-24 pt-3">
      <section className="space-y-5">
        <header className="edge-mobile overflow-hidden rounded-none border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-e1)] sm:rounded-[var(--radius-lg)]">
          <div className="relative p-4 sm:p-5">
            <div className="absolute right-4 top-4 sm:right-5 sm:top-5">
              <ObjectActionMenu
                label="Chronicle actions"
                items={[
                  canEditChronicleSettings
                    ? {
                        label: "Edit chronicle",
                        icon: Edit3,
                        onSelect: () => setSettingsOpen(true),
                      }
                    : null,
                  canDeleteChronicle
                    ? {
                        label: "Delete chronicle",
                        icon: Trash,
                        tone: "danger",
                        onSelect: () => setDeleteChronicleOpen(true),
                      }
                    : null,
                ]}
              />
            </div>

            <Link
              href={`/h/${encodeURIComponent(hubSlug)}?t=chronicles`}
              className="inline-flex items-center gap-2 pr-12 text-sm font-medium text-[var(--brand)] hover:underline"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
              Back to chronicles
            </Link>

            <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)]">
                  Chronicle
                </div>
                <h1 className="mt-2 text-[var(--fg-primary)]" style={{ fontSize: "28px", lineHeight: "34px", fontWeight: 600 }}>
                  {chronicle.name || chronicle.slug}
                </h1>
                <p className="mt-2 text-[var(--fg-secondary)]" style={{ fontSize: "13px", lineHeight: "18px" }}>
                  +{hubSlug} / {chronicle.slug}
                </p>
                <p className="mt-3 max-w-3xl text-[var(--fg-secondary)]" style={{ fontSize: "16px", lineHeight: "24px" }}>
                  {chronicle.description || "No description yet."}
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-[var(--muted)]">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5">
                  <ScanText className="h-4 w-4" strokeWidth={1.8} />
                  {Number(chronicle.incidentCount || 0)} incidents
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5">
                  Updated {formatRelativeDate(chronicle.updatedAt)}
                </span>
                {error ? <span className="text-sm text-red-600">{error}</span> : null}
              </div>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--fg-primary)] hover:border-[var(--brand)] hover:text-[var(--brand)]"
                aria-label="Share chronicle"
              >
                <Share2 className="h-4 w-4" strokeWidth={1.8} />
                Share
              </button>
            </div>
          </div>
        </header>

        <ChronicleSectionMasthead
          title="Incidents"
        />

        {(canFilterIncidentStatus || canContribute) ? (
          <div className="edge-mobile flex flex-col items-start gap-3 px-5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-1">
            {canFilterIncidentStatus ? (
              <div className="flex flex-wrap items-center gap-2">
                {[
                  ["all", "All"],
                  ["draft", "Draft"],
                  ["published", "Published"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setIncidentStatusFilter(value)}
                    className={`inline-flex items-center rounded-full border border-[var(--border)] px-4 py-2 text-sm font-medium ${
                      incidentStatusFilter === value
                        ? "bg-[var(--surface)] text-[var(--fg-primary)]"
                        : "text-[var(--fg-secondary)] hover:border-[var(--brand)] hover:text-[var(--brand)]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
            {canContribute ? (
              <button
                type="button"
                onClick={() => setDrawerState({ open: true, mode: "create", incident: null })}
                className="inline-flex items-center justify-center rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--brand-foreground)] shadow-[var(--shadow-e1)] sm:ml-auto"
              >
                Create Incident
              </button>
            ) : null}
          </div>
        ) : null}

        <section className="edge-mobile space-y-4">
          {loadingIncidents ? (
            [0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-40 animate-pulse rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-e1)]"
              />
            ))
          ) : visibleIncidents.length === 0 ? (
            <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] px-4 py-8 text-center text-sm text-[var(--muted)] shadow-[var(--shadow-e1)] sm:px-6">
              {canFilterIncidentStatus && incidentStatusFilter !== "all"
                ? `No ${incidentStatusFilter} incidents yet.`
                : !canReadDrafts && Number(chronicle.incidentCount || 0) > 0
                ? "No published incidents yet."
                : "No incidents yet."}
              {canContribute ? " Add the first incident to start this timeline." : ""}
            </div>
          ) : (
            visibleIncidents.map((incident) => {
              const canEdit = canContribute && (isChronicleManager || incident.authorUid === user?.uid);
              const canDeleteIncident = isChronicleManager;
              const startDate = toDateSafe(incident?.temporal?.startAt || incident.sortKey);
              const date = buildDateParts(incident?.temporal?.startAt || incident.sortKey);
              return (
                <IncidentCard
                  key={incident.id}
                  date={date}
                  title={incident.title}
                  preview={
                    <LinkifiedCardText
                      content={incident.description || ""}
                      clampLines={5}
                      className="prose max-w-none text-sm leading-6 text-[var(--fg-secondary)] dark:prose-invert [&_a]:text-[var(--brand)] [&_a]:no-underline [&_a]:hover:text-[var(--brand)]"
                    />
                  }
                  metadata={[
                    incident.location ? { icon: MapPin, label: incident.location } : null,
                    canFilterIncidentStatus ? {
                      label: `${String(incident.status || "draft").charAt(0).toUpperCase()}${String(incident.status || "draft").slice(1)}`,
                    } : null,
                    incident.temporal?.approximate ? { label: "Approximate" } : null,
                    !incident.location && startDate
                      ? { label: startDate.toLocaleDateString() }
                      : null,
                  ].filter(Boolean)}
                  href={buildIncidentPath(hubSlug, chronicle.slug || chronicleSlug, incident.id)}
                  actionMenu={
                    <ObjectActionMenu
                      label="Incident actions"
                      items={[
                        canEdit
                          ? {
                              label: "Edit incident",
                              icon: Edit3,
                              onSelect: () => setDrawerState({ open: true, mode: "edit", incident }),
                            }
                          : null,
                        canDeleteIncident
                          ? {
                              label: "Delete incident",
                              icon: Trash,
                              tone: "danger",
                              onSelect: () => setDeleteTarget(incident),
                            }
                          : null,
                      ]}
                    />
                  }
                />
              );
            })
          )}
        </section>
      </section>

      <ChronicleIncidentDrawer
        open={drawerState.open}
        mode={drawerState.mode}
        incident={drawerState.incident}
        existingLocationEntities={availableLocationEntities}
        onClose={() => setDrawerState({ open: false, mode: "create", incident: null })}
        onSubmit={drawerState.mode === "edit" ? handleEditIncident : handleCreateIncident}
        canPublish={canPublish}
        saving={saving}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete incident?"
        message="This removes the incident from the timeline."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        confirming={deletingIncident}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteIncident}
      />

      {shareOpen ? (
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            setSharePayload(null);
          }}
          payload={sharePayload}
        />
      ) : null}

      <ConfirmDialog
        open={deleteChronicleOpen}
        title="Delete chronicle?"
        message="This permanently deletes this chronicle and all incidents under it."
        confirmLabel="Delete Chronicle"
        cancelLabel="Cancel"
        tone="danger"
        confirming={deletingChronicle}
        onCancel={() => setDeleteChronicleOpen(false)}
        onConfirm={handleDeleteChronicle}
      />

      {settingsOpen ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4 py-6 backdrop-blur-sm"
          onClick={() => setSettingsOpen(false)}
        >
          <section
            className="w-full max-w-lg rounded-2xl border border-[var(--border)] bg-[var(--card)] p-5"
            role="dialog"
            aria-modal="true"
            aria-label="Edit chronicle settings"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[var(--fg-primary)]">Edit Chronicle</h3>
            <p className="mt-1 text-sm text-[var(--muted)]">
              Update chronicle name and description. Slug cannot be changed in Phase 1.
            </p>
            <div className="mt-4 space-y-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium">Name</label>
                <input
                  className="input"
                  value={settingsDraft.name}
                  onChange={(event) =>
                    setSettingsDraft((prev) => ({ ...prev, name: event.target.value }))
                  }
                  maxLength={80}
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium">Description</label>
                <textarea
                  className="input min-h-[100px]"
                  value={settingsDraft.description}
                  onChange={(event) =>
                    setSettingsDraft((prev) => ({ ...prev, description: event.target.value }))
                  }
                  maxLength={500}
                  rows={4}
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSettingsOpen(false)}
                  className="rounded-[var(--radius-sm)] border border-[var(--border)] px-3 py-1.5 text-sm text-[var(--fg-primary)] hover:bg-[var(--surface-2)]"
                  disabled={savingSettings}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveChronicleSettings}
                  disabled={savingSettings}
                  className="rounded-[var(--radius)] bg-[var(--brand)] px-4 py-2 text-sm font-medium text-[var(--brand-foreground)] disabled:opacity-60"
                >
                  {savingSettings ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
